import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@/components/ui/icons';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeDropDownList } from '@/components/ui/tee-dropdown-list';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLoader } from '@/components/ui/tee-loader';
import TeeBadge from '@/components/ui/tee-badge';
import { contentItemService } from '@/services/content-item.service';
import { useTaxonomy } from '@/hooks/useTaxonomy';
import { TaxonomyType } from '@/types/taxonomy';
import {
  ContentItem,
  ContentMediaType,
  ContentStatus,
} from '@/types/contentItem';

interface ContentPickerPanelProps {
  excludeIds: string[];
  onAdd: (item: ContentItem) => void;
  disabled?: boolean;
}

const MEDIA_TYPE_LABEL_KEY: Record<ContentMediaType, string> = {
  [ContentMediaType.IMAGE]: 'contentLibrary.mediaTabImage',
  [ContentMediaType.VIDEO]: 'contentLibrary.mediaTypeVideo',
  [ContentMediaType.EMBED]: 'contentLibrary.mediaTabVideoLink',
};

export default function ContentPickerPanel({
  excludeIds,
  onAdd,
  disabled = false,
}: ContentPickerPanelProps) {
  const { t } = useTranslation();
  const { options: taxonomyOptions } = useTaxonomy();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const [results, setResults] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Debounce the search term only — filter dropdown changes fire immediately
  // via the effect below since they are already discrete, low-frequency events.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const loadResults = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await contentItemService.listContentItems({
        status: ContentStatus.PUBLISHED,
        mediaType: mediaType ? (mediaType as ContentMediaType) : undefined,
        categoryId: categoryId || undefined,
        search: debouncedSearch || undefined,
        limit: 20,
        offset: 0,
      });
      setResults(result.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, mediaType, categoryId]);

  const mediaTypeOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...Object.values(ContentMediaType).map((value) => ({
      label: t(MEDIA_TYPE_LABEL_KEY[value]),
      value,
    })),
  ];

  const categoryOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...taxonomyOptions[TaxonomyType.CATEGORY],
  ];

  return (
    <div className="flex flex-col gap-4 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-4">
      <div className="flex flex-col gap-3">
        <TeeInput
          type="text"
          placeholder={t('contentLibrary.searchPlaceholder')}
          leadingIcon={<IconSearch className="w-4 h-4" />}
          inputSize="s"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-3">
          <TeeDropDownList
            options={mediaTypeOptions}
            value={mediaType}
            onChange={(value) => setMediaType(String(value))}
            placeholder={t('contentLibrary.filterMediaType')}
            className="flex-1"
          />
          <TeeDropDownList
            options={categoryOptions}
            value={categoryId}
            onChange={(value) => setCategoryId(String(value))}
            placeholder={t('contentLibrary.filterCategory')}
            className="flex-1"
          />
        </div>
      </div>

      <div className="max-h-[520px] overflow-y-auto flex flex-col gap-2">
        {loading ? (
          <TeeLoader />
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <p className="text-body-s text-[var(--text-content-sub-title)]">
              {t('contentLibrary.errorLoadItems')}
            </p>
            <TeeButton
              variant="secondary"
              size="s"
              text={t('contentLibrary.retry')}
              onClick={loadResults}
            />
          </div>
        ) : results.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-body-s text-[var(--text-content-sub-title)]">
              {t('contentLibrary.emptySearchResults')}
            </p>
          </div>
        ) : (
          results.map((item) => {
            const thumbnailUrl =
              item.videoThumbnailUrl ?? item.imageSignedUrl ?? item.imageUrl;
            const alreadyAdded = excludeIds.includes(item.id);
            return (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-[6px] border border-[var(--border-default)] bg-[var(--background-card)] p-3"
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt={item.title}
                    className="h-10 w-10 shrink-0 rounded-[6px] object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] bg-[var(--background-page-header)]">
                    <TeeBadge
                      label={t(MEDIA_TYPE_LABEL_KEY[item.mediaType])}
                      size="s"
                      type="grey"
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0 flex flex-col items-start gap-1">
                  <p className="text-[16px] font-normal text-[var(--text-content-title)] truncate">
                    {item.title}
                  </p>
                  <TeeBadge
                    label={t(MEDIA_TYPE_LABEL_KEY[item.mediaType])}
                    size="s"
                    type="grey"
                  />
                </div>

                <TeeButton
                  variant="secondary"
                  size="s"
                  text={t(
                    alreadyAdded ? 'contentLibrary.added' : 'contentLibrary.add'
                  )}
                  disabled={disabled || alreadyAdded}
                  onClick={() => onAdd(item)}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
